import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { CheckCircle2, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/payment/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error("Failed to fetch pricing:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (priceType) => {
    if (!user) {
      toast.error("Please sign in to continue");
      navigate(`/login?redirect=/pricing`);
      return;
    }

    setCheckingOut(priceType);
    try {
      const response = await axios.post(
        `${API}/payment/checkout`,
        {
          price_type: priceType,
          origin_url: window.location.origin
        },
        { headers: getAuthHeaders() }
      );
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Failed to start checkout");
      setCheckingOut(null);
    }
  };

  const proOneoff = pricing?.options?.find(o => o.id === "pro_oneoff");
  const proMonthly = pricing?.options?.find(o => o.id === "pro_monthly");

  return (
    <div className="py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-4">Simple, Founder-Friendly Pricing</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Start for free with our Quick Test and Full Assessment. Upgrade when you need more.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Pricing Cards */}
            <div className="grid md:grid-cols-3 gap-8">
              {/* Free Tier */}
              <Card data-testid="pricing-free">
                <CardHeader>
                  <CardTitle>Free</CardTitle>
                  <CardDescription>Test your business idea</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{pricing?.currency_symbol || "£"}0</span>
                    <span className="text-muted-foreground">/forever</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {[
                      "Quick Test (5 min, no signup)",
                      "Full Assessment (15 min)",
                      "FoundrScore (0-100)",
                      "Section-by-section breakdown",
                      "Basic one-page business plan",
                      "Risk flags & next steps"
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/quiz">
                    <Button variant="outline" className="w-full rounded-full" data-testid="pricing-free-cta">
                      Start Free Quiz
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Pro One-off */}
              <Card className="border-2 border-primary relative" data-testid="pricing-pro-oneoff">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                  RECOMMENDED
                </div>
                <CardHeader>
                  <CardTitle>{proOneoff?.name || "Pro (One-off)"}</CardTitle>
                  <CardDescription>{proOneoff?.description || "Lifetime access"}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{proOneoff?.price_formatted || "£29"}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {(proOneoff?.features || [
                      "Everything in Free",
                      "Tailored Business Plan (PDF)",
                      "Validation Canvas",
                      "30-Day Action Plan",
                      "Day-30 Re-scoring"
                    ]).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full rounded-full" 
                    onClick={() => handleCheckout("pro_oneoff")}
                    disabled={checkingOut === "pro_oneoff"}
                    data-testid="pricing-pro-oneoff-cta"
                  >
                    {checkingOut === "pro_oneoff" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Get Pro Access
                        <Sparkles className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Pro Monthly */}
              <Card data-testid="pricing-pro-monthly">
                <CardHeader>
                  <CardTitle>{proMonthly?.name || "Pro (Monthly)"}</CardTitle>
                  <CardDescription>{proMonthly?.description || "Ongoing access"}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{pricing?.currency_symbol || "£"}{proMonthly?.price || 9}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {(proMonthly?.features || [
                      "Everything in Pro One-off",
                      "Unlimited Re-scoring",
                      "Score History Tracking",
                      "New Features First"
                    ]).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-secondary" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    variant="outline" 
                    className="w-full rounded-full"
                    onClick={() => handleCheckout("pro_monthly")}
                    disabled={checkingOut === "pro_monthly"}
                    data-testid="pricing-pro-monthly-cta"
                  >
                    {checkingOut === "pro_monthly" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Subscribe Monthly
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Advisory Call CTA */}
            <Card className="mt-8 bg-muted/50" data-testid="pricing-advisory">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-lg mb-1">Need hands-on support?</h3>
                    <p className="text-sm text-muted-foreground">
                      Book a 1:1 validation strategy call with an expert
                    </p>
                  </div>
                  <a href="https://calendly.com" target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="rounded-full whitespace-nowrap">
                      Book Advisory Call
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* FAQ Preview */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold mb-4">Questions?</h2>
          <p className="text-muted-foreground mb-6">
            We're here to help. Reach out at hello@foundrscore.com
          </p>
        </div>
      </div>
    </div>
  );
}
